/**
 * `window.__spades.audio` under `?e2e=1`: the engine, live gains, a silent generated sprite so
 * `play` is audible without assets, and `?speak=<userId>` to simulate a Discord SPEAKING_START
 * once audio is unlocked (used until the MockActivityBridge drives `voiceDucking`).
 * Every `play` is already recorded in `audio.lastPlayed` by the engine (scene/testApi).
 *
 * ## Why this is three functions rather than one
 *
 * A probe must not appear before the thing it describes exists, because a spec waits on probes.
 *
 * The announcer probes (`clipCount`, `clipLoaded`, `registerClips`) used to be installed only by
 * `installAudio`, which is what attaches the `Announcer` to the cue queue. `announcer.spec.ts`
 * therefore polls `clipLoaded` and gets, for free, "the announcer is attached". Publishing them
 * from the boot path instead broke that: the pack is discovered and decoded by
 * `shell/announcerManifest.ts` long before the table scene is built, so the gate went green while
 * nothing was listening for barks and the first one was dropped.
 *
 * So `installEngineTestApi` carries what is true as soon as an `AudioEngine` exists and is safe to
 * publish at boot (the music director does), and `installAnnouncerTestApi` carries what is only
 * true once the announcer is attached. `installAudioTestApi` is both, and stays the one call
 * `installAudio` and the dev pages make.
 */
import { isE2E, testRoot } from '../scene/testApi';
import { announcerClips, registerAnnouncerClips, type AnnouncerClipRegistration } from './announcerClips';
import type { AudioEngine, Bus, DuckedBus } from './AudioEngine';
import type { MusicDirector, MusicTrack } from './MusicDirector';

export const SILENT_SPRITE = { silent: [0, 0.25] as [number, number], blip: [0.25, 0.1] as [number, number] };

/** Registers a silent 0.5s buffer as sprite `test` (ids `silent`, `blip`). Needs a context. */
export function registerSilentSprite(engine: AudioEngine): boolean {
  const ctx = engine.ensureContext();
  if (!ctx) return false;
  const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * 0.5), ctx.sampleRate);
  engine.registerSprite('test', buffer, SILENT_SPRITE);
  return true;
}

let gestureWired = false;

/**
 * Engine-level probes: everything that is true the moment an `AudioEngine` exists.
 *
 * Safe to publish from the boot path, and the music director does, because the beds start on the
 * menu and a spec standing there needs `isUnlocked` and `busGain`. Deliberately carries nothing
 * about the announcer — see the note at the top of this file.
 */
export function installEngineTestApi(engine: AudioEngine): void {
  if (!isE2E()) return;
  const root = testRoot();
  root.audio ??= { lastPlayed: [] };
  root.audio.lastPlayed ??= [];
  Object.assign(root.audio, {
    engine,
    unlock: () => {
      engine.unlock();
      registerSilentSprite(engine);
    },
    tryUnlock: () => engine.tryUnlock(),
    isUnlocked: () => engine.isUnlocked,
    state: () => engine.ctx?.state ?? 'none',
    currentTime: () => engine.ctx?.currentTime ?? -1,
    busGain: (bus: Bus) => engine.busGain(bus),
    duckGain: (bus: DuckedBus) => engine.duckGain(bus),
    setSpeaking: (userId: string, on: boolean) => engine.setSpeaking(userId, on),
    setVolume: (which: Bus | 'master', v: number) => engine.setVolume(which, v),
    play: (id: string, opts?: Parameters<AudioEngine['play']>[1]) => !!engine.play(id, opts),
    // Whether the sfx sprite actually registered its ids: `play` reports `audible:false` both for
    // a missing sample and for a locked context, so a spec needs to tell the two apart.
    has: (id: string) => engine.has(id),
    sampleIds: () => engine.sampleIds(),
  });

  // Once per document, not once per installer: this is now called from the boot path as well as
  // from the table scene, and two `once` listeners would unlock twice and register the silent
  // sprite twice.
  if (gestureWired) return;
  gestureWired = true;
  const speak = new URLSearchParams(location.search).get('speak');
  const onGesture = (): void => {
    engine.unlock();
    registerSilentSprite(engine);
    if (speak) engine.setSpeaking(speak, true);
  };
  document.addEventListener('pointerdown', onGesture, { once: true, capture: true });
}

/**
 * Announcer probes. Installed only where the `Announcer` is attached to the cue queue, so a spec
 * that polls `clipLoaded` is also waiting for something to be listening for barks.
 */
export function installAnnouncerTestApi(engine: AudioEngine): void {
  if (!isE2E()) return;
  const root = testRoot();
  root.audio ??= { lastPlayed: [] };
  root.audio.lastPlayed ??= [];
  Object.assign(root.audio, {
    // Announcer voice packs: `shell/announcerManifest.ts` calls this with the flattened manifest.
    registerClips: (clips: AnnouncerClipRegistration[]) => registerAnnouncerClips(clips, { engine }).loading,
    clipCount: () => announcerClips.size,
    clipLoaded: (id: string) => announcerClips.isLoaded(id),
  });
}

/** Both halves. What `installAudio` and the dev pages call. */
export function installAudioTestApi(engine: AudioEngine): void {
  installEngineTestApi(engine);
  installAnnouncerTestApi(engine);
}

// --- music agent -------------------------------------------------------------------------------
/**
 * `window.__spades.audio.music` under `?e2e=1`: read-only probes for the music beds.
 *
 * Read-only on purpose. A spec asserts which bed the state machine chose and how loud it is; it
 * never drives the director, because a spec that can force a track proves nothing about the
 * mapping that is the actual behaviour under test.
 */
export function installMusicTestApi(director: MusicDirector): void {
  if (!isE2E()) return;
  const root = testRoot();
  root.audio ??= { lastPlayed: [] };
  Object.assign(root.audio, {
    music: {
      /** The bed faded up right now, or null when the music is off. */
      track: (): MusicTrack | null => director.current,
      /** Beds with a source built (i.e. something has been fetched for them). */
      loaded: (): MusicTrack[] => director.loaded,
      /** That bed's own fader, 0..1, separate from the music bus volume. */
      gain: (track: MusicTrack): number => director.gainOf(track),
    },
  });
}
// --- end music agent ---------------------------------------------------------------------------
